import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Gauge, ThumbsUp, ThumbsDown, ShieldAlert } from "lucide-react";

const BAND_STYLES = {
  A: "bg-emerald-600", B: "bg-teal-600", C: "bg-amber-500", D: "bg-orange-600", E: "bg-red-600",
};

const COMPONENT_LABELS = {
  bureau: "Bureau",
  cashflow: "Cash flow",
  income_stability: "Income stability",
  foir: "FOIR",
  vehicle: "Vehicle",
  fraud_doc: "Fraud / docs",
  source_quality: "Source quality",
  residence_stability: "Residence stability",
};

export default function RiskScoreCard({ appId, app, onRefresh, busyState }) {
  const [busy, setBusy] = busyState;
  const risk = app.risk_score;

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/applications/${appId}/risk-score/run`);
      toast.success("CarFin Risk Score computed");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Risk scoring failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="risk-score-card">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Gauge size={18} /> CarFin Risk Score</div>
          <div className="text-sm text-slate-600">Combined AI risk engine — bureau, cash flow, vehicle, fraud & source quality.</div>
        </div>
        <Button onClick={run} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="run-risk-score-btn">
          {risk ? "Re-score" : "Run risk score"}
        </Button>
      </div>

      {!risk ? (
        <div className="text-sm text-slate-500" data-testid="risk-score-empty">Not scored yet. Run valuation + cash flow first for the most complete score.</div>
      ) : (
        <div className="space-y-4" data-testid="risk-score-result">
          <div className="flex flex-wrap items-center gap-4">
            <div className={`text-white rounded-xl px-5 py-3 ${BAND_STYLES[risk.band] || "bg-slate-700"}`} data-testid="risk-score-value">
              <div className="text-3xl font-bold font-mono leading-none">{risk.carfin_score}</div>
              <div className="text-[11px] uppercase tracking-wider opacity-90 mt-1">of 1000</div>
            </div>
            <div>
              <div className="text-xl font-semibold text-slate-900">Band {risk.band} · {risk.band_label}</div>
              <div className="text-sm text-slate-600">{risk.segment} · Recommendation: <span className="font-semibold capitalize">{risk.recommendation}</span>{risk.manual_review && " · Manual review required"}</div>
              <div className="text-sm text-slate-600 mt-0.5">
                Rec. ROI {risk.recommended_roi ? `${risk.recommended_roi}%` : "—"}
                {risk.roi_range ? ` (${risk.roi_range[0]}–${risk.roi_range[1]}%)` : ""} · Max LTV {Math.round((risk.recommended_ltv || 0) * 100)}% · Tenure up to {risk.recommended_tenure_months} mo
                {risk.recommended_loan_amount ? ` · Rec. loan ${formatINR(risk.recommended_loan_amount)}` : ""}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(risk.components || {}).map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-200 p-2.5" data-testid={`risk-component-${k}`}>
                <div className="flex justify-between text-xs text-slate-600 mb-1">
                  <span>{COMPONENT_LABELS[k] || k}</span>
                  <span className="font-mono font-semibold">{v}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100">
                  <div className={`h-1.5 rounded-full ${v >= 65 ? "bg-emerald-500" : v >= 45 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${Math.min(100, v)}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(risk.positive_factors || []).length > 0 && (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="risk-positives">
                <div className="font-semibold mb-1 flex items-center gap-1"><ThumbsUp size={14} /> Positive factors</div>
                <ul className="list-disc pl-5 space-y-1">{risk.positive_factors.map((f) => <li key={f}>{f}</li>)}</ul>
              </div>
            )}
            {(risk.negative_factors || []).length > 0 && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-900 text-sm" data-testid="risk-negatives">
                <div className="font-semibold mb-1 flex items-center gap-1"><ThumbsDown size={14} /> Negative factors</div>
                <ul className="list-disc pl-5 space-y-1">{risk.negative_factors.map((f) => <li key={f}>{f}</li>)}</ul>
              </div>
            )}
          </div>

          {(risk.exception_triggers || []).length > 0 && (
            <div className="p-3 rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-sm" data-testid="risk-exceptions">
              <div className="font-semibold mb-1 flex items-center gap-1"><ShieldAlert size={14} /> Exception approval required{risk.exception_level ? ` — ${risk.exception_level}` : ""}</div>
              <ul className="list-disc pl-5 space-y-1">{risk.exception_triggers.map((t) => <li key={t.trigger}>{t.description}</li>)}</ul>
            </div>
          )}

          {(risk.stipulations || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {risk.stipulations.map((s) => (
                <span key={s} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
